/**
 * Public pricing page.
 *
 * 3-Tier Packaging:
 * - Free (personal): $0 forever for individual developers on repos they own, plus the
 *   free 2-seat workspace for company repositories; the install command is copied here
 * - Team: $8/seat/mo (annual) or $10/mo, 3–200 seats, for seat pooling, repo defaults & policies
 * - Enterprise: sold on features, not headcount — SAML SSO, SCIM, SIEM, offline licences, the
 *   self-hosted CAVS DB Server (#922, #1170). No service-level terms and no master agreement exist yet.
 *
 * Choosing Team never opens Stripe on its own: the calculator holds the order, a
 * signed-out visitor signs in and comes back to it pre-filled, and checkout starts on
 * a click (#1141).
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { useSession } from '@/app/providers'
import { LANGUAGES } from '@/entities/languages'
import { CALCULATOR, FIRST_TIME_RIGHT, PRIVACY_CONTRACT } from '@/entities/metrics'
import { planPricing } from '@/entities/pricing'
import { type BillingInterval } from '@/entities/types'
import { useAccount, useCatalog, useCheckout, useSubmitEnterpriseLead } from '@/shared/api/hooks'
import { capture } from '@/shared/lib/analytics'
import { useSeenOnce } from '@/shared/lib/useSeenOnce'
import { stackLabels } from '@/shared/lib/stackTable'
import { PublicPage } from '@/shared/ui/Chrome'
import { CheckIcon } from '@/shared/ui/icons'
import {
  Button,
  ErrorNotice,
  Field,
  Label,
  Modal,
  Notice,
  formatPrice,
} from '@/shared/ui/primitives'
import { Turnstile } from '@/shared/ui/Turnstile'

/** The same command /downloads shows for macOS and Linux. */
const INSTALL_COMMAND = 'curl -fsSL https://archis.dev/install.sh | sh'

/** How the reader pays for their agent: a flat subscription, or tokens on an API key. */
type CalculatorMode = 'flat' | 'api'

/** Rows of the plan table: only what differs between plans. `null` is a dash. */
const PLAN_DIFFERENCES: { row: string; cells: [string | null, string | null, string | null] }[] = [
  { row: 'Repositories', cells: ['Yours, plus company repos for 2 seats', "Yours and your company's", "Yours and your company's"] },
  { row: 'Repositories as one system', cells: [null, 'Federation', 'Federation, on your own CAVS DB Server'] },
  { row: 'People', cells: ['1, or 2 at work', '3–200', 'Any'] },
  { row: 'Devices', cells: ['2 each', '3 per seat', 'Up to 10 each, set by your admin'] },
  { row: 'Agents set up on clone', cells: [null, '✓', '✓'] },
  { row: 'Workspace policies', cells: [null, '✓', '✓'] },
  { row: 'Audit log', cells: [null, 'CSV export', 'Streamed to your SIEM'] },
  { row: 'Sign-in', cells: ['Email or Google', 'Email or Google', 'SAML SSO, SCIM'] },
  { row: 'Offline use', cells: ['4 days', '4 days', 'Air-gapped licence'] },
  { row: 'Support', cells: ['Community', '1 business day', 'Founder-led'] },
]

/** Answers ordered by the objection that blocks a purchase; the flat-plan one answers in the lead's currency first (#1162). */
const FAQ = [
  {
    q: 'Does a third teammate mean paying for three seats?',
    a: 'Yes. A workspace holds two seats for free, with no card. Adding a third person moves it to Team, where every seat is paid: three seats is three times the per-seat price.',
  },
  {
    q: 'I use a flat-rate plan like Cursor Pro or Claude Max. What do I gain?',
    a: `Fewer reworked changes, and a limit that lasts longer. In our latest paired sessions with Claude Code, the mean answer score went from ${FIRST_TIME_RIGHT.quality.without.toFixed(2)} on the agent's own tools to ${FIRST_TIME_RIGHT.quality.with.toFixed(2)} with Archis, one repository so far. The plan's price does not move, but the same usage limit covers more sessions, so fewer engineers need the top tier of Claude Max or Cursor and fewer run into overage. A typical session used about ${Math.round(CALCULATOR.expected * 100)}% fewer tokens, read-heavy tasks about half.`,
  },
  {
    q: 'Does Archis ever transmit our source code to your servers?',
    a: 'Never. Source code parsing, AST lowering, symbol queries, and session history remain strictly local on your disk. Our cloud API only manages seat licensing, team authentication, and release updates. There is literally no backend endpoint designed to receive source code.',
  },
  {
    q: 'What happens if our team subscription ends or is canceled?',
    a: 'If a subscription lapses, your account automatically drops to Archis Free. Your local AST indexes, SQLite knowledge graphs, and tool output caches remain intact on your machine — nothing is deleted. Only organization-owned repository queries and team policy sync pause until renewed.',
  },
  {
    q: 'Do I still need to pay for Claude, OpenAI, or Cursor subscriptions?',
    a: 'Yes. Archis is a local semantic context compiler, not an LLM vendor. You keep your existing agent subscriptions, API keys, and configurations. Archis connects locally via the Model Context Protocol (MCP) and prevents your agents from wasting thousands of tokens re-reading entire files.',
  },
  {
    q: 'What counts as a device?',
    a: 'A machine you have signed in on with chi auth login. Free allows 2 active devices and Team 3 per seat; you can revoke an old one from the portal at any time.',
  },
  {
    q: 'Can we run Archis in an air-gapped, VPN, or offline environment?',
    a: 'Offline, yes: a signed lease keeps licensed commands working for up to four days without a connection (valid for 24 hours, then a 72-hour grace), and local data is never deleted when it runs out. Fully air-gapped networks need an offline licence file, which is part of Enterprise.',
  },
]

export function Pricing() {
  const [params, setParams] = useSearchParams()
  const initialInterval = (params.get('interval') as BillingInterval) || 'annual'
  const [interval, setInterval] = useState<BillingInterval>(
    initialInterval === 'monthly' || initialInterval === 'annual' ? initialInterval : 'annual',
  )
  const [mode, setMode] = useState<CalculatorMode>(params.get('mode') === 'api' ? 'api' : 'flat')
  const [enterpriseOpen, setEnterpriseOpen] = useState(false)
  const [enterpriseSubmitted, setEnterpriseSubmitted] = useState(false)
  const [inquiryName, setInquiryName] = useState('')
  const [inquiryEmail, setInquiryEmail] = useState('')
  const [inquiryCompany, setInquiryCompany] = useState('')
  const [inquiryTeamSize, setInquiryTeamSize] = useState('')
  const [inquiryNotes, setInquiryNotes] = useState('')
  const [cfToken, setCfToken] = useState('')
  const [honeypot, setHoneypot] = useState('')

  // The order: `?seats=` is how a visitor coming back from sign-in finds it filled in.
  const initialSeatsParam = parseInt(params.get('seats') ?? '', 10)
  const [calcSeats, setCalcSeats] = useState(isNaN(initialSeatsParam) ? 10 : initialSeatsParam)
  const [calcSpendPerDev, setCalcSpendPerDev] = useState(250)

  const [copied, setCopied] = useState(false)
  const copiedTimer = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(copiedTimer.current), [])

  const teamWhy = useSeenOnce<HTMLDivElement>('first_time_right_viewed', { page: 'pricing' })

  const { data: catalog, error } = useCatalog()
  const { data: account } = useAccount()
  const { user } = useSession()
  const navigate = useNavigate()
  const checkout = useCheckout()
  const enterpriseLead = useSubmitEnterpriseLead()

  const discount = catalog?.annual_discount_percent
  const firstPlan = catalog?.plans[0]
  const annualDiscount =
    discount ?? (firstPlan ? planPricing(firstPlan, 'annual')?.discountPercent ?? 20 : 20)

  const teamPlan = catalog?.plans?.find((p) => p.code === 'team')
  const teamPricing = teamPlan ? planPricing(teamPlan, interval, discount) : null
  // The hero quotes the yearly per-month price whichever interval is selected.
  const annualTeamPricing = teamPlan ? planPricing(teamPlan, 'annual', discount) : null
  const minPaidSeats = teamPlan?.min_paid_seats ?? 3
  const maxPaidSeats = teamPlan?.max_paid_seats ?? 200
  const seats = Math.min(Math.max(calcSeats, minPaidSeats), maxPaidSeats)

  const isTeamSubscriber =
    account?.plan === 'team' &&
    (account?.subscription_status === 'active' || account?.subscription_status === 'trialing')
  const orgId = account?.organizations?.[0]?.id

  // Seat prices, in cents, derived from the catalog with the $8 / $10 list prices as fallback.
  const seatPerMonthCents = teamPricing?.perMonthCents ?? (interval === 'annual' ? 800 : 1000)
  const seatChargedCents = teamPricing?.chargedCents ?? (interval === 'annual' ? 9600 : 1000)
  const seatMonthToMonthCents = teamPricing?.monthlyCents || 1000
  const heroSeatPrice = formatPrice(annualTeamPricing?.perMonthCents ?? 800)
  const teamBilledLine =
    interval === 'annual'
      ? `${formatPrice(seatChargedCents)} per seat, billed yearly · ${formatPrice(seatMonthToMonthCents)} month to month`
      : `${formatPrice(seatChargedCents)} per seat, billed monthly`

  // Flat plan: the price does not move, the usage limit goes further. A session that
  // uses x fewer tokens does 1/(1−x) times the work before the limit lands.
  const headroomTypical = (1 / (1 - CALCULATOR.expected)).toFixed(2)
  const headroomReadHeavy = (1 / (1 - CALCULATOR.high)).toFixed(1)
  // Across the team, the extra sessions add up to this many usage limits' worth of
  // work: the share of engineers who no longer need the top tier, at most.
  const extraLimits = (seats * (1 / (1 - CALCULATOR.expected) - 1)).toFixed(1)
  const typicalPercent = Math.round(CALCULATOR.expected * 100)
  const readHeavyPercent = Math.round(CALCULATOR.high * 100)

  // Pay per token: the measured per-session token saving (CALCULATOR: 22% typical,
  // 50.6% on tasks both arms solved) applies only to the share of the bill that is
  // repository work, half by default. Exposure figures like the 89–95% task-context
  // band overstate a bill 2–3x.
  const seatMonthlyPrice = seatPerMonthCents / 100
  const totalMonthlySeatCost = seats * seatMonthlyPrice
  const repositoryShare = seats * calcSpendPerDev * CALCULATOR.defaultContextShare
  const estimatedMonthlyDollarsSaved = Math.round(repositoryShare * CALCULATOR.expected)
  const savedHigh = Math.round(repositoryShare * CALCULATOR.high)
  const netMonthlyBenefit = estimatedMonthlyDollarsSaved - totalMonthlySeatCost
  // Agent spend per engineer at which the expected saving covers one seat.
  const breakEvenSpendPerDev = Math.ceil(seatMonthlyPrice / (CALCULATOR.defaultContextShare * CALCULATOR.expected))

  // Every plan CTA on this page reports which plan, from where, and on which
  // interval — the pricing half of the funnel the landing's plan block already has.
  function trackPlanCta(
    plan: 'free' | 'free_workspace' | 'team' | 'enterprise',
    location: string,
    ctaSeats?: number,
  ) {
    capture('pricing_plan_cta_clicked', { plan, location, interval, seats: ctaSeats, signed_in: Boolean(user) })
  }

  async function copyInstall(location: string) {
    capture('install_command_copied', { location })
    try {
      await navigator.clipboard.writeText(INSTALL_COMMAND)
      setCopied(true)
      clearTimeout(copiedTimer.current)
      copiedTimer.current = setTimeout(() => setCopied(false), 1600)
    } catch {
      // Clipboard access can be denied; the command is visible and selectable
      // anyway, so there is nothing to recover from.
    }
  }

  function chooseMode(next: CalculatorMode) {
    if (next === mode) return
    setMode(next)
    capture('calculator_mode_changed', { mode: next })
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        updated.set('mode', next)
        return updated
      },
      { replace: true },
    )
  }

  function jumpToSeats() {
    document.getElementById('seats')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Coming back from sign-in lands on `#seats`. The router's ScrollToHash runs before
  // this lazily loaded page has rendered the target, so the page scrolls itself once.
  useEffect(() => {
    if (location.hash !== '#seats') return
    const frame = requestAnimationFrame(() =>
      document.getElementById('seats')?.scrollIntoView({ block: 'start' }),
    )
    return () => cancelAnimationFrame(frame)
  }, [])

  function onChooseTeam(orderSeats: number) {
    if (!user) {
      // Back to this order after sign-in, filled in; checkout waits for a click.
      navigate(
        `/signin?next=${encodeURIComponent(
          `/pricing?plan=team&interval=${interval}&mode=${mode}&seats=${orderSeats}#seats`,
        )}`,
      )
      return
    }
    if (isTeamSubscriber) {
      navigate('/billing')
      return
    }
    checkout.mutate({
      plan: 'team',
      interval,
      seats: orderSeats,
      ...(orgId ? { organizationId: orgId } : {}),
    })
  }

  function submitEnterprise(e: React.FormEvent) {
    e.preventDefault()
    // The thank-you screen appears only after the backend stored the lead: the
    // form promises a follow-up, and that promise needs a record behind it.
    enterpriseLead.mutate(
      {
        name: inquiryName,
        email: inquiryEmail,
        company: inquiryCompany,
        teamSize: inquiryTeamSize,
        notes: inquiryNotes,
        cfToken,
        website: honeypot,
      },
      { onSuccess: () => setEnterpriseSubmitted(true) },
    )
  }

  const copyLabel = copied ? 'Copied' : 'Copy install command'
  const toggleClass = (on: boolean) => (on ? 'btn btn--sm btn--primary' : 'btn btn--sm')
  const toggleStyle = { border: 'none', borderRadius: 0 }

  return (
    <PublicPage>
      {/* ────────────────────────────────── Hero ─────────────────────────── */}
      <section className="shell" style={{ paddingBlock: 'var(--s8) var(--s6)' }}>
        <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
          <Label index="01">Pricing</Label>

          <h1 style={{ fontSize: 'clamp(2.3rem, 5.2vw, 3.75rem)', lineHeight: 1.15, maxWidth: '28ch' }}>
            Free on your own repositories.
            <br />
            <span className="accent-text">Per seat when your company&apos;s code joins.</span>
          </h1>

          <p className="lead" style={{ maxWidth: '60ch' }}>
            Your repositories cost nothing. At work, two engineers share a free workspace. From the third, every seat
            is {heroSeatPrice} a month, up to {maxPaidSeats} seats without talking to sales.
          </p>

          <div className="row" style={{ gap: 'var(--s3)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="mono-note signal">
              {PRIVACY_CONTRACT.value} {PRIVACY_CONTRACT.meaning}
            </span>
            <Link to="/verify" className="mono-note" style={{ color: 'var(--text-dim)' }}>
              See what the CLI sends →
            </Link>
          </div>
        </div>

        {/* Interval toggle */}
        <div className="row" style={{ marginTop: 'var(--s6)', gap: 'var(--s3)', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="row" style={{ gap: 0, border: '1px solid var(--rule-bright)', borderRadius: 'var(--radius)' }}>
            {(['annual', 'monthly'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setInterval(option)}
                className={toggleClass(interval === option)}
                style={toggleStyle}
                aria-pressed={interval === option}
              >
                {option === 'annual' ? 'Yearly' : 'Monthly'}
              </button>
            ))}
          </div>
          <span className="mono-note">Yearly saves {annualDiscount}%</span>
        </div>
      </section>

      {/* ────────────────────────────────── Plan cards ───────────────────── */}
      <section className="shell">
        {checkout.isPending && (
          <div style={{ marginBottom: 'var(--s5)' }}>
            <Notice tone="ok">Opening secure Stripe Checkout for your Archis Team Workspace...</Notice>
          </div>
        )}
        <ErrorNotice error={error ?? checkout.error} />

        <div className="grid-3" style={{ gap: 'var(--s4)', alignItems: 'stretch' }}>
          {/* Free */}
          <div className="card stack persona-card" style={{ padding: 'var(--s5)', ['--gap' as string]: 'var(--s4)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
              <Label index="01">Free</Label>
              <div className="price" style={{ fontSize: '2.25rem', fontWeight: 600, letterSpacing: '-0.05em' }}>
                $0
                <span className="price__per"> forever</span>
              </div>
              <p className="mono-note" style={{ margin: 0 }}>
                Repositories you own · 2 devices
              </p>
            </div>

            <ul className="feature-list" style={{ margin: 0 }}>
              {[
                'Unlimited repositories you own',
                'At work: 2 free seats on company repositories, no card, no time limit',
                'No account until you run chi auth login',
              ].map((feature) => (
                <li key={feature} className="feature-item" style={{ gap: 'var(--s3)' }}>
                  <span className="tick" aria-hidden="true" />
                  <span style={{ fontSize: '0.875rem' }}>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)', marginTop: 'auto' }}>
              <div className="term" style={{ border: '1px solid var(--rule)', borderRadius: 'var(--radius)' }}>
                <div className="term__body" style={{ padding: 'var(--s2) var(--s3)' }}>
                  <span
                    className="term__line"
                    style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}
                  >
                    <span className="term__prompt">$ </span>
                    {INSTALL_COMMAND}
                  </span>
                </div>
              </div>
              <Button variant="primary" block onClick={() => copyInstall('plan_card')}>
                {copyLabel}
              </Button>
              <Link
                to="/team"
                className="btn btn--ghost btn--block btn--sm"
                style={{ whiteSpace: 'normal', height: 'auto', textAlign: 'center', lineHeight: 1.4 }}
                onClick={() => trackPlanCta('free_workspace', 'plan_card')}
              >
                Using it at work? Create the free 2-seat workspace →
              </Link>
            </div>
          </div>

          {/* Team */}
          <div
            className="card stack persona-card persona-card--featured"
            style={{ padding: 'var(--s5)', ['--gap' as string]: 'var(--s4)' }}
          >
            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
              <Label index="02">Team</Label>
              <div className="price" style={{ fontSize: '2.25rem', fontWeight: 600, letterSpacing: '-0.05em' }}>
                {formatPrice(seatPerMonthCents)}
                <span className="price__per"> / seat / month</span>
              </div>
              <p className="mono-note" style={{ margin: 0 }}>
                {teamBilledLine}
              </p>
            </div>

            <div className="stack" ref={teamWhy} style={{ ['--gap' as string]: 'var(--s2)' }}>
              <p style={{ margin: 0, fontSize: '0.9375rem', lineHeight: 1.5 }}>
                Agents that get old code right the first time, set up the same way on every clone.
              </p>
              <p className="mono-note" style={{ margin: 0, lineHeight: 1.5 }}>
                Answer score {FIRST_TIME_RIGHT.quality.without.toFixed(2)} → {FIRST_TIME_RIGHT.quality.with.toFixed(2)}{' '}
                with Archis. <Link to="/verify" className="signal">Measured on one repository →</Link>
              </p>
            </div>

            <ul className="feature-list" style={{ margin: 0 }}>
              {[
                `${minPaidSeats} to ${maxPaidSeats} seats, self-serve`,
                'Agents set up on clone, from committed repo defaults',
                'Workspace policies',
                'Audit log with CSV export',
                'Reassign seats, revoke devices',
              ].map((feature) => (
                <li key={feature} className="feature-item" style={{ gap: 'var(--s3)' }}>
                  <span className="tick" aria-hidden="true" />
                  <span style={{ fontSize: '0.875rem' }}>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)', marginTop: 'auto' }}>
              <Button
                variant="primary"
                block
                onClick={() => {
                  trackPlanCta('team', 'plan_card')
                  jumpToSeats()
                }}
              >
                Choose seats →
              </Button>
              <p className="mono-note center" style={{ margin: 0, fontSize: '0.75rem' }}>
                Stop paying and you drop to Free. Your index stays.
              </p>
            </div>
          </div>

          {/* Enterprise */}
          <div
            className="card stack persona-card"
            id="enterprise"
            style={{ padding: 'var(--s5)', ['--gap' as string]: 'var(--s4)' }}
          >
            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
              <Label index="03">Enterprise</Label>
              <div className="price" style={{ fontSize: '2.25rem', fontWeight: 600, letterSpacing: '-0.05em' }}>
                Custom
                <span className="price__per"> yearly</span>
              </div>
              <p className="mono-note" style={{ margin: 0 }}>
                Any size · sold on features
              </p>
            </div>

            <ul className="feature-list" style={{ margin: 0 }}>
              {[
                'SAML SSO and SCIM',
                'Self-hosted CAVS DB Server for federation',
                'Audit streaming to your SIEM',
                'Offline licence for air-gapped networks',
                'DPA on request (in counsel review), invoice billing',
              ].map((feature) => (
                <li key={feature} className="feature-item" style={{ gap: 'var(--s3)' }}>
                  <span className="tick" aria-hidden="true" />
                  <span style={{ fontSize: '0.875rem' }}>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)', marginTop: 'auto' }}>
              <Button
                block
                onClick={() => {
                  trackPlanCta('enterprise', 'plan_card')
                  setEnterpriseOpen(true)
                }}
              >
                Talk to a founder →
              </Button>
              <p className="mono-note center" style={{ margin: 0, fontSize: '0.75rem' }}>
                Reply within one business day
              </p>
            </div>
          </div>
        </div>

        {/* What every plan runs */}
        <div
          className="card row"
          style={{ marginTop: 'var(--s4)', padding: 'var(--s4) var(--s5)', gap: 'var(--s3) var(--s6)', flexWrap: 'wrap', alignItems: 'center' }}
        >
          <span className="mono-note" style={{ color: 'var(--text)' }}>
            Every plan runs the full engine:
          </span>
          {[
            `${LANGUAGES.length} languages`,
            'Claude Code, Cursor, Codex CLI, Gemini CLI',
            'Reversible test and log compression',
            'Local index and dashboard',
          ].map((item) => (
            <span key={item} className="mono-note">
              {item}
            </span>
          ))}
        </div>
      </section>

      {/* ────────────────────────────────── Seats calculator ─────────────── */}
      <section className="shell" id="seats" style={{ paddingBlock: 'var(--s8)' }}>
        <div className="glass stack" style={{ ['--gap' as string]: 'var(--s5)', padding: 'var(--s6)' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
            <Label index="05">What a seat buys</Label>
            <h2 style={{ margin: 0 }}>How you pay for your agent decides what Archis saves you.</h2>
          </div>

          <div
            className="row"
            style={{ gap: 0, border: '1px solid var(--rule-bright)', borderRadius: 'var(--radius)', alignSelf: 'flex-start', flexWrap: 'wrap' }}
          >
            <button
              type="button"
              className={toggleClass(mode === 'flat')}
              style={toggleStyle}
              aria-pressed={mode === 'flat'}
              onClick={() => chooseMode('flat')}
            >
              Flat plan (Claude Max, Cursor Pro)
            </button>
            <button
              type="button"
              className={toggleClass(mode === 'api')}
              style={toggleStyle}
              aria-pressed={mode === 'api'}
              onClick={() => chooseMode('api')}
            >
              Pay per token (API key)
            </button>
          </div>

          <div className="grid-2" style={{ gap: 'var(--s6)', alignItems: 'start' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
              <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
                <div className="row row--between">
                  <span className="readout__caption">Engineers</span>
                  <span className="fig" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                    {seats}
                  </span>
                </div>
                <input
                  className="calc__slider"
                  type="range"
                  min={minPaidSeats}
                  max={maxPaidSeats}
                  step={1}
                  value={seats}
                  style={{ ['--fill' as string]: `${((seats - minPaidSeats) / (maxPaidSeats - minPaidSeats)) * 100}%` }}
                  onChange={(e) => setCalcSeats(Number(e.target.value))}
                  aria-label="Engineers"
                />
                <div className="row row--between mono-note">
                  <span>{minPaidSeats}</span>
                  <span>{maxPaidSeats} · above that, Enterprise</span>
                </div>
              </div>

              {mode === 'api' ? (
                <>
                  <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
                    <div className="row row--between">
                      <span className="readout__caption">Agent spend per engineer, monthly</span>
                      <span className="fig" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                        ${calcSpendPerDev}
                      </span>
                    </div>
                    <input
                      className="calc__slider"
                      type="range"
                      min={50}
                      max={800}
                      step={25}
                      value={calcSpendPerDev}
                      style={{ ['--fill' as string]: `${((calcSpendPerDev - 50) / 750) * 100}%` }}
                      onChange={(e) => setCalcSpendPerDev(Number(e.target.value))}
                      aria-label="Agent spend per engineer"
                    />
                    <div className="row row--between mono-note">
                      <span>$50</span>
                      <span>$800</span>
                    </div>
                  </div>
                  <p className="mono-note" style={{ margin: 0, lineHeight: 1.6 }}>
                    Assumes {CALCULATOR.defaultContextShare === 0.5 ? 'half' : `${Math.round(CALCULATOR.defaultContextShare * 100)}%`}{' '}
                    the bill is repository work, where a typical session uses {typicalPercent}% fewer tokens (
                    {readHeavyPercent}% on read-heavy tasks). A seat pays for itself from about ${breakEvenSpendPerDev} per
                    engineer a month.{' '}
                    <Link to="/verify" className="signal">
                      How this was measured →
                    </Link>
                  </p>
                </>
              ) : (
                <>
                  <p className="dim" style={{ margin: 0, lineHeight: 1.6 }}>
                    The agent reads less of the repository for each task, so the same usage limit covers more sessions.
                    Fewer engineers need the top tier of Claude Max or Cursor, and fewer run into overage. And the answers
                    hold up: mean score {FIRST_TIME_RIGHT.quality.without.toFixed(2)} without Archis,{' '}
                    {FIRST_TIME_RIGHT.quality.with.toFixed(2)} with it, in the same sessions.
                  </p>
                  <p className="mono-note" style={{ margin: 0, lineHeight: 1.6 }}>
                    Typical session {typicalPercent}% fewer tokens, read-heavy tasks {readHeavyPercent}% fewer, measured
                    with Claude Code.{' '}
                    <Link to="/verify" className="signal">
                      How this was measured →
                    </Link>
                  </p>
                </>
              )}
            </div>

            {/* Readout and order */}
            <div className="card stack" style={{ ['--gap' as string]: 'var(--s4)', background: 'var(--panel)', padding: 'var(--s5)' }}>
              {mode === 'api' ? (
                <>
                  <div className="stack" style={{ ['--gap' as string]: 'var(--s1)' }}>
                    <span className="readout__caption">
                      {netMonthlyBenefit >= 0 ? 'Net saving after seats' : 'Seats cost more than they save at this spend'}
                    </span>
                    <div
                      className="price"
                      style={{ fontSize: '2.5rem', fontWeight: 600, color: netMonthlyBenefit >= 0 ? 'var(--signal)' : 'var(--text-dim)' }}
                    >
                      {netMonthlyBenefit >= 0 ? '+' : '−'}${Math.abs(netMonthlyBenefit).toLocaleString()}
                      <span className="price__per"> / month</span>
                    </div>
                  </div>
                  <div className="grid-2" style={{ gap: 'var(--s3)', borderTop: '1px solid var(--rule)', paddingTop: 'var(--s3)' }}>
                    <div>
                      <div className="fig signal" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                        ${estimatedMonthlyDollarsSaved.toLocaleString()}
                      </div>
                      <div className="readout__caption">Tokens saved (up to ${savedHigh.toLocaleString()})</div>
                    </div>
                    <div>
                      <div className="fig" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                        ${totalMonthlySeatCost.toLocaleString()}
                      </div>
                      <div className="readout__caption">Team seats</div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="stack" style={{ ['--gap' as string]: 'var(--s1)' }}>
                    <span className="readout__caption">Sessions per usage limit, typical session</span>
                    <div className="price signal" style={{ fontSize: '2.5rem', fontWeight: 600 }}>
                      {headroomTypical}×
                    </div>
                  </div>
                  <div className="grid-2" style={{ gap: 'var(--s3)', borderTop: '1px solid var(--rule)', paddingTop: 'var(--s3)' }}>
                    <div>
                      <div className="fig signal" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                        {headroomReadHeavy}×
                      </div>
                      <div className="readout__caption">On read-heavy tasks</div>
                    </div>
                    <div>
                      <div className="fig" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                        {extraLimits}
                      </div>
                      <div className="readout__caption">Usage limits&apos; worth of extra sessions across {seats} engineers</div>
                    </div>
                  </div>
                </>
              )}

              <div className="stack" style={{ ['--gap' as string]: 'var(--s3)', borderTop: '1px solid var(--rule)', paddingTop: 'var(--s4)' }}>
                <span className="readout__caption">Your order</span>
                <div className="row row--between">
                  <span className="dim" style={{ fontSize: '0.875rem' }}>
                    {seats} seats × {formatPrice(seatPerMonthCents)}
                  </span>
                  <span className="fig" style={{ fontWeight: 600 }}>
                    {formatPrice(seats * seatPerMonthCents)} / mo
                  </span>
                </div>
                <div className="row row--between">
                  <span className="mono-note">
                    {interval === 'annual' ? `${formatPrice(seats * seatChargedCents)} billed yearly` : 'Billed monthly'}
                  </span>
                  <span className="mono-note">Cancel anytime</span>
                </div>
                <Button
                  variant="primary"
                  block
                  loading={checkout.isPending}
                  onClick={() => {
                    trackPlanCta('team', 'calculator', seats)
                    onChooseTeam(seats)
                  }}
                >
                  {isTeamSubscriber ? `Manage seats in billing →` : `Continue to checkout · ${seats} seats`}
                </Button>
                {!user && (
                  <p className="mono-note center" style={{ margin: 0, fontSize: '0.75rem' }}>
                    Signed out? You sign in first and come back to this order. Nothing is charged until Stripe asks.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────── What changes between plans ───── */}
      <section className="shell" id="matrix" style={{ paddingBottom: 'var(--s8)' }}>
        <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
          <Label index="06">What changes between plans</Label>
          <div className="table-wrap">
            <table
              className="table table--stack"
              style={{ width: '100%', fontSize: '0.875rem', ...stackLabels(['Free', 'Team', 'Enterprise']) }}
            >
              <thead>
                <tr>
                  <th style={{ width: '36%' }} aria-label="Capability" />
                  <th style={{ textAlign: 'center' }}>Free</th>
                  <th style={{ textAlign: 'center' }}>Team</th>
                  <th style={{ textAlign: 'center' }}>Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {PLAN_DIFFERENCES.map(({ row, cells }) => (
                  <tr key={row}>
                    <td>{row}</td>
                    {cells.map((cell, i) => (
                      <td key={i} style={{ textAlign: 'center' }} className={cell === null ? 'dim' : undefined}>
                        {cell ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────── Questions ────────────────────── */}
      <section className="shell" style={{ paddingBottom: 'var(--s8)' }}>
        <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
          <Label index="07">Questions</Label>
          <div className="faq">
            {FAQ.map((item) => (
              <details className="faq__item" key={item.q}>
                <summary>{item.q}</summary>
                <div className="faq__answer">
                  <p>{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────────────── Closing panel ────────────────── */}
      <section className="shell" style={{ paddingBottom: 'var(--s9)' }}>
        <div className="panel panel--signal center stack" style={{ padding: 'var(--s7) var(--s5)', ['--gap' as string]: 'var(--s4)', alignItems: 'center' }}>
          <h2 style={{ maxWidth: '30ch', margin: 0 }}>Install in one line. Sign in from the terminal.</h2>
          <div
            className="term"
            style={{ border: '1px solid var(--rule)', borderRadius: 'var(--radius)', width: 'fit-content', maxWidth: '100%', marginInline: 'auto' }}
          >
            <div className="term__body" style={{ padding: 'var(--s3) var(--s4)', overflowX: 'auto' }}>
              <span className="term__line">
                <span className="term__prompt">$ </span>
                {INSTALL_COMMAND}
              </span>
            </div>
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 'var(--s3)', flexWrap: 'wrap' }}>
            <button type="button" className="cta cta--primary" onClick={() => copyInstall('closing')}>
              {copyLabel}
            </button>
            <Link to="/team" className="btn" onClick={() => trackPlanCta('free_workspace', 'closing')}>
              Create free workspace →
            </Link>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────── Enterprise Modal ────────────── */}
      {enterpriseOpen && (
        <Modal
          title="Contact Enterprise Sales"
          onClose={() => {
            setEnterpriseOpen(false)
            setEnterpriseSubmitted(false)
            enterpriseLead.reset()
          }}
        >
          {enterpriseSubmitted ? (
            <div className="stack" style={{ ['--gap' as string]: 'var(--s4)', padding: 'var(--s4) 0' }}>
              <div className="row" style={{ gap: 'var(--s2)', color: 'var(--signal)', alignItems: 'center' }}>
                <CheckIcon className="signal" style={{ width: 24, height: 24 }} />
                <h3 style={{ margin: 0 }}>Thank you for reaching out!</h3>
              </div>
              <p className="dim">
                We have your request for <strong>{inquiryCompany || 'your team'}</strong>. A founder will reply to{' '}
                <strong>{inquiryEmail}</strong> within one business day to set up a pilot and a security review.
              </p>
              <Button variant="primary" onClick={() => setEnterpriseOpen(false)}>
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={submitEnterprise} className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
              <p className="dim" style={{ margin: 0, fontSize: '0.875rem' }}>
                Tell us about your organization's AI agent setup, security mandates, or monorepo requirements:
              </p>

              <Field label="Your Name">
                <input
                  className="input"
                  type="text"
                  required
                  placeholder="Ada Lovelace"
                  value={inquiryName}
                  onChange={(e) => setInquiryName(e.target.value)}
                />
              </Field>

              <Field label="Work Email">
                <input
                  className="input"
                  type="email"
                  required
                  placeholder="ada@company.com"
                  value={inquiryEmail}
                  onChange={(e) => setInquiryEmail(e.target.value)}
                />
              </Field>

              <div className="grid-2" style={{ gap: 'var(--s3)' }}>
                <Field label="Company Name">
                  <input
                    className="input"
                    type="text"
                    required
                    placeholder="Acme Corp"
                    value={inquiryCompany}
                    onChange={(e) => setInquiryCompany(e.target.value)}
                  />
                </Field>

                <Field label="Engineering Team Size">
                  <select
                    className="input"
                    value={inquiryTeamSize}
                    onChange={(e) => setInquiryTeamSize(e.target.value)}
                    required
                  >
                    <option value="" disabled>
                      Select…
                    </option>
                    <option value="1-19">Under 20 developers</option>
                    <option value="20-50">20–50 developers</option>
                    <option value="50-200">50–200 developers</option>
                    <option value="200-1000">200–1,000 developers</option>
                    <option value="1000+">1,000+ developers</option>
                  </select>
                </Field>
              </div>

              <Field label="Specific Requirements (Optional)">
                <textarea
                  className="input"
                  rows={3}
                  placeholder="SAML / Okta SSO, security review, a self-hosted CAVS DB Server..."
                  value={inquiryNotes}
                  onChange={(e) => setInquiryNotes(e.target.value)}
                />
              </Field>

              {/* Anti-bot Honeypot field (invisible to humans) */}
              <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
                <label htmlFor="modal-website">Website</label>
                <input
                  id="modal-website"
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              {/* Cloudflare Turnstile Bot Protection */}
              <Turnstile onVerify={setCfToken} onExpire={() => setCfToken('')} />

              <ErrorNotice error={enterpriseLead.error} />

              <div className="row row--between" style={{ marginTop: 'var(--s2)' }}>
                <Button type="button" variant="ghost" onClick={() => setEnterpriseOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={enterpriseLead.isPending}>
                  Submit Request →
                </Button>
              </div>
            </form>
          )}
        </Modal>
      )}
    </PublicPage>
  )
}
